import type { HaulerTemplateDefinition } from './HaulerTemplateDefinition'
import { HaulerTemplateSectionEnum } from '../enums/HaulerTemplateSectionEnum'

// Mirrored in the mobile app's apps/totalmilk/hauler-mobile/src/template/seeds.ts (LIQUID_TEMPLATE) — no mobile entry point exports it; keep in sync if it changes.
export const LIQUID_TEMPLATE: HaulerTemplateDefinition = {
  // Sentinel — overwritten with generateId() when created/migrated; the read-path
  // MILK_TEMPLATE fallback (ResolveHaulerTemplate) returns it unresolved.
  id: '',
  code: 'liquid',
  name: 'Liquid Template',
  weightUnit: 'gal',
  quantityUnit: 'gal',
  labels: {
    ticketSingular: 'Ticket',
    ticketPlural: 'Tickets',
    pickupSingular: 'Pickup',
    pickupPlural: 'Pickups',
    deliverySingular: 'Delivery',
    deliveryPlural: 'Deliveries',
    producerTypeLabel: 'Producer',
    plantTypeLabel: 'Delivery Point',
  },
  fields: [
    // Ticket section
    {
      key: 'ticketDate',
      label: 'Ticket Date',
      required: true,
      webVisible: true,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 0,
    },
    {
      key: 'ticketDrivers',
      label: 'Drivers',
      required: true,
      webVisible: true,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 1,
    },
    {
      key: 'ticketHauler',
      label: 'Hauler',
      required: false,
      webVisible: true,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 2,
    },
    {
      key: 'ticketRemarks',
      label: 'Ticket Remarks',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 3,
    },
    {
      key: 'ticketManifestNo',
      label: 'Manifest No',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 4,
    },
    {
      key: 'ticketTrailerNo',
      label: 'Trailer No',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 5,
    },
    {
      key: 'ticketDriverOrHelperName',
      label: 'Driver/Helper Name',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 6,
    },
    {
      key: 'ticketSealNo',
      label: 'Seal No',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 7,
    },
    {
      key: 'ticketStartingWeight',
      label: 'Starting Weight',
      unit: 'gal',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 8,
    },
    {
      key: 'ticketSamplerLicenseNo',
      label: 'Sampler License No.',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 9,
    },
    {
      key: 'ticketLicenseNoExpirationDate',
      label: 'License Expiration',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 10,
    },
    {
      key: 'ticketDriverSignature',
      label: 'Driver signature',
      required: true,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 11,
    },
    // Pickup section
    {
      key: 'pickupDate',
      label: 'Pickup Date',
      required: true,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 1,
      validation: { dateWindow: { other: 'ticketDate', daysBefore: 3, daysAfter: 3 } },
    },
    {
      key: 'pickupRemarks',
      label: 'Pickup Remarks',
      required: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 2,
    },
    {
      key: 'pickupQuantity',
      label: 'Volume (gal)',
      unit: 'gal',
      required: true,
      webVisible: true,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 3,
    },
    {
      key: 'pickupGradeType',
      label: 'Grade Type',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 4,
    },
    {
      key: 'pickupExtraGradeType',
      label: 'Extra Grade Type',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 5,
    },
    {
      key: 'pickupUnitId',
      label: 'Unit ID',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 6,
    },
    {
      key: 'pickupTemperature',
      label: 'Temperature',
      unit: '°F',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 7,
      validation: { min: 0, max: 80 },
    },
    {
      key: 'pickupGaugeRod',
      label: 'Gauge Rod',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 8,
    },
    {
      key: 'pickupBarcode',
      label: 'Barcode',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 9,
    },
    {
      key: 'pickupCpd',
      label: 'CPD',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 10,
    },
    {
      key: 'pickupNumberOfMilkings',
      label: 'No. of Milkings',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 11,
    },
    // Delivery section
    {
      key: 'deliveryDate',
      label: 'Delivery Date',
      required: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 1,
      validation: { dateWindow: { other: 'ticketDate', daysBefore: 3, daysAfter: 3 } },
    },
    {
      key: 'deliveryGrossWeight',
      label: 'Gross Weight',
      unit: 'gal',
      required: false,
      webVisible: false,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 2,
    },
    {
      key: 'deliveryTareWeight',
      label: 'Tare Weight',
      unit: 'gal',
      required: false,
      webVisible: false,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 3,
    },
    {
      key: 'deliveryNetWeight',
      label: 'Net Weight',
      unit: 'gal',
      required: false,
      webVisible: false,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 4,
    },
    {
      key: 'deliveryQuantity',
      label: 'Volume Delivered (gal)',
      unit: 'gal',
      required: true,
      webVisible: true,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 5,
    },
    {
      key: 'deliveryRemarks',
      label: 'Delivery Remarks',
      required: false,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 6,
    },
    {
      key: 'deliveryBtu',
      label: 'BTU',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 7,
    },
    {
      key: 'deliverySilo',
      label: 'Silo',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 8,
    },
    {
      key: 'deliveryDrugTest',
      label: 'Drug Test',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 9,
    },
    {
      key: 'deliveryDrugResult',
      label: 'Drug Result',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 10,
    },
    {
      key: 'deliverySignature',
      label: 'Delivery Signature',
      required: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 11,
    },
    // Customer section — profile fields available but hidden by default for Liquid
    {
      key: 'customerUnitIds',
      label: 'Unit IDs',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 1,
    },
    {
      key: 'customerBtus',
      label: 'BTUs',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 2,
    },
    {
      key: 'customerSilos',
      label: 'Silos',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 3,
    },
    {
      key: 'customerSyncInfo',
      label: 'TMMS Sync Info',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 4,
    },
    {
      key: 'customerPlantCode',
      label: 'Plant Code',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 5,
    },
    {
      key: 'customerReportId',
      label: 'Report ID',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 6,
    },
    // Driver section — driver profile fields available but hidden by default for Liquid
    {
      key: 'driverSamplerLicenseNo',
      label: 'Sampler License No.',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Driver,
      order: 1,
    },
    {
      key: 'driverLicenseExpirationDate',
      label: 'License Expiry',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Driver,
      order: 2,
    },
    {
      key: 'driverTicketHaulerName',
      label: 'Hauler Name',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Driver,
      order: 3,
    },
    {
      key: 'driverTicketHaulerAddress',
      label: 'Hauler Address',
      required: false,
      webVisible: false,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Driver,
      order: 4,
    },
  ],
}
